import React, { useEffect, useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import DayWisePerformanceTable from "./DayWisePerformanceTable";
import BookingSourcePerformance from "./BookingSourcePerformance";
import PropertyWisePerformance from "./PropertyWisePerformance";
import useGroupPerformanceData from "./hooks/useGroupPerformanceData";
import { useSelector } from "react-redux";
import MetricsContainer from "./MetricsContainer";
import PropertyPerformanceMetrics from "./PropertyPerformanceMetrics";
import PerformanceDashboardHeader from "./PerformanceDashboardHeader";
import usePerformanceDashboardHeader from "./hooks/usePerformanceDashboardHeader";
import { DATE_FORMATS } from "../../../Utils/CONST";
import { format, subYears } from "date-fns";

const GroupPerformanceDashboard = () => {
  const auth = useSelector((state) => state.auth);
  const allHotelIds = useMemo(
    () =>
      auth.hotels
        .filter((hotel) => "active" === hotel.status.toLowerCase())
        .map((hotel) => hotel.hotelId),
    [auth.hotels]
  );
  const [propertyFilterItems, setPropertyFilterItems] = useState(allHotelIds);
  const hotels = auth?.hotels || [];
  const groupId = useMemo(() => {
    if (hotels.length > 0) {
      return hotels[0]?.hotelId || "";
    }
    const stored = localStorage.getItem("mainGroupId");
    if (!stored || stored === "null" || stored === "undefined") return "";
    return stored;
  }, [hotels]);
  const emailId = useMemo(() => {
    const fromHotel = hotels.find((hotel) => hotel?.emailId)?.emailId;
    if (fromHotel) return fromHotel;
    const stored = localStorage.getItem("groupEmail");
    if (!stored || stored === "null" || stored === "undefined") return "";
    return stored;
  }, [hotels]);
  const { data, loading } = useGroupPerformanceData(
    propertyFilterItems,
    undefined,
    groupId,
    emailId
  );
  const { data: comparisonData, loading: isComparisonDataLoading } =
    useGroupPerformanceData(
      propertyFilterItems,
      format(subYears(new Date(), 1), DATE_FORMATS.DATE_2),
      groupId,
      emailId
    );
  const {
    propertyFilterMenuAnchor,
    period,
    propertyFilterLabel,
    allPropertyFilter,
    propertyFilterItemsTemp,
    doComparison,
    handlePropertyFilterMenuClose,
    handlePropertyFilterMenuOpen,
    handlePeriodChange,
    toggleAllPropertyFilter,
    handleSelectPropertyInFilter,
    toggleDoComparison,
  } = usePerformanceDashboardHeader(allHotelIds);

  useEffect(() => {
    if (!propertyFilterMenuAnchor) {
      setPropertyFilterItems(propertyFilterItemsTemp);
    }
  }, [propertyFilterMenuAnchor, propertyFilterItemsTemp]);

  // const propertyFilterDisplayLabel = (() => {
  //   let label = "";
  //   if (allPropertyFilter) label = "All properties";
  //   else {
  //     let hotelNames = [];
  //     for (const hotelId of propertyFilter) {
  //       let hotel = auth.hotels.find((hotel) => hotel.hotelId === hotelId);
  //       if (hotel) hotelNames.push(hotel.hotelName);
  //     }
  //     label = hotelNames.join(",");
  //   }
  //   return label;
  // })();

  const getTopPerformingProperties = (metric) => {
    let topPerforming = [];

    switch (metric.toLowerCase()) {
      case "today":
        topPerforming = [...(data?.hotelWiseMetricsBreakdown?.td || [])].sort(
          (a, b) => b.occupancy - a.occupancy
        );
        break;

      case "mtd":
        topPerforming = [...(data?.hotelWiseMetricsBreakdown?.mtd || [])].sort(
          (a, b) => b.occupancy - a.occupancy
        );
        break;
      case "ytd":
        topPerforming = [...(data?.hotelWiseMetricsBreakdown?.ytd || [])].sort(
          (a, b) => b.occupancy - a.occupancy
        );
        break;

      default:
        break;
    }
    topPerforming.forEach((item) => {
      const hotel = auth.hotels.find((hotel) => hotel.hotelId === item.hotelId);
      item.hotelName = hotel.hotelName;
    });
    return topPerforming.length > 3 ? topPerforming.slice(0, 3) : topPerforming;
  };
  const getLowPerformingProperties = (metric) => {
    let lowPerforming = [];

    switch (metric.toLowerCase()) {
      case "today":
        lowPerforming = [...(data?.hotelWiseMetricsBreakdown?.td || [])].sort(
          (a, b) => a.occupancy - b.occupancy
        );
        break;

      case "mtd":
        lowPerforming = [...(data?.hotelWiseMetricsBreakdown?.mtd || [])].sort(
          (a, b) => a.occupancy - b.occupancy
        );
        break;
      case "ytd":
        lowPerforming = [...(data?.hotelWiseMetricsBreakdown?.ytd || [])].sort(
          (a, b) => a.occupancy - b.occupancy
        );
        break;

      default:
        break;
    }
    lowPerforming.forEach((item) => {
      const hotel = auth.hotels.find((hotel) => hotel.hotelId === item.hotelId);
      item.hotelName = hotel.hotelName;
    });
    return lowPerforming.length > 3 ? lowPerforming.slice(0, 3) : lowPerforming;
  };

  const metricsData = (data) => {
    switch (period.toLowerCase()) {
      case "today":
        return data?.groupMetricsBreakDown?.groupMetricsTd;
      case "mtd":
        return data?.groupMetricsBreakDown?.groupMetricsMtd;
      case "ytd":
        return data?.groupMetricsBreakDown?.groupMetricsYtd;
      default:
        return data?.groupMetricsBreakDown?.groupMetricsTd;
    }
  };

  return (
    <Stack spacing={4} overflow={"scroll"}>
      <PerformanceDashboardHeader
        period={period}
        propertyFilterItemsTemp={propertyFilterItemsTemp}
        propertyFilterMenuAnchor={propertyFilterMenuAnchor}
        propertyFilterLabel={propertyFilterLabel}
        allPropertyFilter={allPropertyFilter}
        handlePropertyFilterMenuClose={handlePropertyFilterMenuClose}
        handlePropertyFilterMenuOpen={handlePropertyFilterMenuOpen}
        handleSelectPropertyInFilter={handleSelectPropertyInFilter}
        handlePeriodChange={handlePeriodChange}
        toggleAllPropertyFilter={toggleAllPropertyFilter}
        toggleDoComparison={toggleDoComparison}
      />

      <MetricsContainer
        metrics={metricsData(data)}
        isLoading={loading || isComparisonDataLoading}
        doComparison={doComparison}
        comparisonMetrics={metricsData(comparisonData)}
      />

      <PropertyPerformanceMetrics
        topPerforming={getTopPerformingProperties(period)}
        lowPerforming={getLowPerformingProperties(period)}
        chartData={data?.groupDayWisePerformanceBreakdown}
        doComparison={doComparison}
        comparisonChartData={comparisonData?.groupDayWisePerformanceBreakdown}
        dataKey={"revenue"}
        isLoading={loading || isComparisonDataLoading}
        showPerformanceLists={propertyFilterItems.length === allHotelIds.length}
      />

      <PropertyWisePerformance
        data={data}
        isLoading={loading}
        doComparison={doComparison}
        comparisonData={comparisonData}
      />

      <BookingSourcePerformance
        data={data}
        period={period}
        isLoading={loading || isComparisonDataLoading}
        doComparison={doComparison}
        comparisonData={comparisonData}
      />

      <DayWisePerformanceTable
        tableData={data?.groupDayWisePerformanceBreakdown || []}
        isLoading={loading || isComparisonDataLoading}
        doComparison={doComparison}
        comparisonTableData={
          comparisonData?.groupDayWisePerformanceBreakdown || []
        }
      />

      <Box width={3}></Box>
    </Stack>
  );
};

export default GroupPerformanceDashboard;
